import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import {
  Button,
  Card,
  Checkbox,
  Table,
  TableHeader,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
} from '@agion/ui'
import type { InfraOperation, TerraformRecoveryPlan } from '@/core/infra_types'
import { cancelOperation, createOperation } from './infra_operation.service'

// Addresses and import IDs are displayed, never accepted as user input.
export function TerraformRecoveryPanel({
  operation,
  onRefresh,
}: {
  operation: InfraOperation
  onRefresh?: () => Promise<void>
}) {
  const { t } = useTranslation(['pages'])
  const navigate = useNavigate()
  const [approved, setApproved] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [now, setNow] = useState(Date.now)
  const requestIds = useRef({ inspect: crypto.randomUUID(), reconcile: crypto.randomUUID() })
  const inFlight = useRef(false)
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 1000)
    return () => window.clearInterval(timer)
  }, [])

  const isInspection = operation.operationType === 'terraform_recovery_inspect'
  const isReconcile = operation.operationType === 'terraform_recovery_reconcile'
  const originalFailure =
    operation.operationType === 'terraform_apply' &&
    operation.status === 'failed' &&
    operation.parameters['module'] === 'azure-vm'
  if (!isInspection && !isReconcile && !originalFailure) return null
  const sourceId = originalFailure ? operation.id : operation.parameters['sourceOperationId']
  if (typeof sourceId !== 'string') return null
  const raw = operation.output?.['recovery'] as TerraformRecoveryPlan | undefined
  const plan =
    raw?.version === 1 &&
    Array.isArray(raw.imports) &&
    Array.isArray(raw.blockers) &&
    Array.isArray(raw.absent)
      ? raw
      : undefined
  const complete = operation.status === 'succeeded'
  const running = operation.status === 'pending' || operation.status === 'running'
  const expiry = operation.completedAt ? Date.parse(operation.completedAt) + 15 * 60_000 : 0
  const expired = !Number.isFinite(expiry) || now >= expiry
  const canApprove =
    isInspection &&
    complete &&
    !!plan &&
    plan.blockers.length === 0 &&
    plan.imports.length > 0 &&
    !expired

  async function submit(mode: 'inspect' | 'reconcile') {
    if (inFlight.current || (mode === 'reconcile' && (!approved || !canApprove))) return
    inFlight.current = true
    setBusy(true)
    setError('')
    try {
      const created = await createOperation({
        requestId: requestIds.current[mode],
        environmentId: operation.environmentId,
        operationType:
          mode === 'inspect' ? 'terraform_recovery_inspect' : 'terraform_recovery_reconcile',
        parameters: {
          sourceOperationId: sourceId,
          ...(mode === 'reconcile'
            ? { inspectionOperationId: operation.id, fingerprint: plan?.fingerprint }
            : {}),
        },
      })
      navigate(`/operations/${created.id}`)
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : t('pages:operations.recovery.submitError'))
    } finally {
      inFlight.current = false
      setBusy(false)
    }
  }

  async function checkInterrupted() {
    if (inFlight.current) return
    inFlight.current = true
    setBusy(true)
    setError('')
    try {
      // The server refuses while River can still execute. This only clears a
      // stranded UI status; it does not cancel Terraform or release its hold.
      await cancelOperation(operation.id)
      await onRefresh?.()
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : t('pages:operations.recovery.submitError'))
    } finally {
      inFlight.current = false
      setBusy(false)
    }
  }

  return (
    <Card className="my-4 p-4 space-y-4">
      <h2 className="text-lg font-semibold">{t('pages:operations.recovery.title')}</h2>
      <p className="text-sm text-slate-600 dark:text-slate-300">
        {t('pages:operations.recovery.explanation')}
      </p>
      {running && <p role="status">{t('pages:operations.recovery.running')}</p>}
      {running && onRefresh && (
        <Button variant="outline" disabled={busy} onClick={() => void checkInterrupted()}>
          {t('pages:operations.recovery.checkInterrupted')}
        </Button>
      )}
      {plan && (
        <>
          <dl className="grid gap-2 text-sm sm:grid-cols-2">
            <div>
              <dt className="font-medium">{t('pages:operations.recovery.subscription')}</dt>
              <dd className="font-mono break-all">{plan.scope.subscription}</dd>
            </div>
            <div>
              <dt className="font-medium">{t('pages:operations.recovery.resourceGroup')}</dt>
              <dd className="font-mono break-all">{plan.scope.resourceGroup}</dd>
            </div>
          </dl>
          <h3 className="font-medium">
            {t(
              isReconcile && complete
                ? 'pages:operations.recovery.reconciled'
                : 'pages:operations.recovery.imports',
            )}
          </h3>
          {plan.imports.length === 0 ? (
            <p>{t('pages:operations.recovery.noImports')}</p>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('pages:operations.recovery.address')}</TableHead>
                    <TableHead>{t('pages:operations.recovery.importId')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {plan.imports.map((resource) => (
                    <TableRow key={resource.address}>
                      <TableCell className="font-mono align-top">{resource.address}</TableCell>
                      <TableCell className="font-mono break-all min-w-64">
                        {resource.importId}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
          {plan.blockers.length > 0 && (
            <div role="alert" className="text-red-700 dark:text-red-300">
              <h3 className="font-medium">{t('pages:operations.recovery.blocked')}</h3>
              <ul className="list-disc pl-5">
                {plan.blockers.map((reason) => (
                  <li key={reason}>{reason}</li>
                ))}
              </ul>
            </div>
          )}
          {plan.absent.length > 0 && (
            <details>
              <summary>
                {t('pages:operations.recovery.absent', { count: plan.absent.length })}
              </summary>
              <ul className="list-disc pl-5 text-sm font-mono">
                {plan.absent.map((address) => (
                  <li key={address}>{address}</li>
                ))}
              </ul>
            </details>
          )}
          {isInspection && complete && expired && (
            <p role="alert">{t('pages:operations.recovery.expired')}</p>
          )}
          {canApprove && (
            <div className="space-y-3">
              <Checkbox
                id="approve-recovery"
                checked={approved}
                disabled={busy}
                onCheckedChange={(checked) => setApproved(checked === true)}
                label={t('pages:operations.recovery.acknowledge')}
              />
              <Button disabled={!approved || busy} onClick={() => void submit('reconcile')}>
                {t('pages:operations.recovery.approve')}
              </Button>
            </div>
          )}
          {complete && plan.freshPlan && (
            <div className="space-y-2">
              <h3 className="font-medium">{t('pages:operations.recovery.freshPlan')}</h3>
              <p className="text-sm">{t('pages:operations.recovery.separateApply')}</p>
              <ul className="list-disc pl-5 text-sm font-mono">
                {plan.provisioning?.map((change) => (
                  <li key={change.address}>
                    {change.address}: {change.actions.join(', ')}
                  </li>
                ))}
              </ul>
              {!plan.provisioning?.length && <p>{t('pages:operations.recovery.noChanges')}</p>}
              <Button
                variant="outline"
                onClick={() => navigate(`/environments/${operation.environmentId}`)}
              >
                {t('pages:operations.recovery.environment')}
              </Button>
            </div>
          )}
        </>
      )}
      {!running && !(isReconcile && complete) && (
        <Button variant="outline" disabled={busy} onClick={() => void submit('inspect')}>
          {t('pages:operations.recovery.inspect')}
        </Button>
      )}
      {error && (
        <p role="alert" className="text-sm text-red-700 dark:text-red-300">
          {error}
        </p>
      )}
    </Card>
  )
}
